import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import {
  OrderableItemListItemImage,
  STANDARD_IMAGE_SIZE,
} from '@/components/orders/orderable-item-list-item-image';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface Props {
  previewItem: ItemListOptionMetadata;
  searchTerm: string;
}

export const InboundDetailCorrectionPreview: React.FC<Props> = ({
  previewItem,
  searchTerm,
}) => {
  const [styles, theme] = useStyles();

  const casePrice =
    typeof previewItem.unitPriceInMicros === 'number'
      ? formatCurrency(
          previewItem.unitCount * previewItem.unitPriceInMicros,
          previewItem.currencyCode,
        )
      : null;

  const packSegment = casePrice
    ? `${casePrice}/${previewItem.packDisplayString}`
    : previewItem.packDisplayString;

  const subtitle = [previewItem.externalID, packSegment]
    .filter(Boolean)
    .join(' • ');

  return (
    <View style={styles.container} testID="inbound-detail-correction-preview">
      <View style={styles.messageRow}>
        <Typography
          color={theme.sk.color.text.secondary}
          variant="paragraph-md"
        >
          {'↳'}
        </Typography>

        <Typography
          color={theme.sk.color.text.secondary}
          variant="paragraph-md"
        >
          {i18n.t('correction_flow.item_will_match_preview', { searchTerm })}
        </Typography>
      </View>

      <View style={styles.previewRow}>
        <OrderableItemListItemImage
          alt={previewItem.displayName}
          hidePlaceholderImages={false}
          imageSize={STANDARD_IMAGE_SIZE}
          imageURLs={previewItem.photoURLs}
          style={styles.image}
        />

        <View style={styles.titleColumn}>
          <Typography numberOfLines={2} variant="paragraph-md-600">
            {previewItem.displayName}
          </Typography>

          {subtitle ? (
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-sm"
            >
              {subtitle}
            </Typography>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignSelf: 'stretch',
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    gap: 12,
    padding: theme.sk.spacing.spaceSMedium,
  },
  image: {
    borderRadius: theme.sk.radii.radiusMedium,
  },
  messageRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  previewRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  titleColumn: {
    alignItems: 'flex-start',
    flex: 1,
    gap: 2,
  },
}));
